import { ReactNode } from 'react';

// 🎯 Tipos para el sorting (reutilizado de VirtualTable)
export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
    field: string;
    direction: SortDirection;
}

export interface VirtualListSorting {
    sorting?: SortConfig | null;
    onSortingChange?: (sorting: SortConfig | null) => void;
}

// 🎯 Interface Segregation Principle (ISP)
// Separamos las props en interfaces específicas por responsabilidad

// 🎯 Render Props Pattern - Core del componente
export interface RenderItemProps<T = any> {
    item: T;
    index: number;
    isLoading?: boolean;
}

export type RenderItemFunction<T = any> = (props: RenderItemProps<T>) => ReactNode;

export interface VirtualListData<T = any> {
    data: T[];
    renderItem: RenderItemFunction<T>;
    total?: number;
    itemKey?: (item: T, index: number) => string | number;
}

export interface VirtualListDimensions {
    height?: string;
    itemHeight?: number;
    gap?: number;
    overscan?: number;
    autoHeight?: boolean;
    maxHeight?: string;
    minHeight?: string;
    estimateItemHeight?: number;
}

export interface VirtualListPagination {
    fetchNextPage?: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    infiniteScrollThreshold?: number;
}

export interface VirtualListStyling {
    className?: string;
    emptyMessage?: string;
    listClassName?: string;
    itemClassName?: string;
}

// 🎯 Main component props usando composition
export interface VirtualListProps<T = any>
    extends VirtualListData<T>,
    VirtualListDimensions,
    VirtualListPagination,
    VirtualListStyling,
    VirtualListSorting { }

// 🎯 Hook interfaces - Fix para permitir refs que pueden ser null
export interface UseContainerDimensionsReturn {
    width: number;
    height: number;
}

export interface UseInfiniteScrollOptions {
    parentRef: React.RefObject<HTMLDivElement | null>;
    fetchNextPage?: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    threshold?: number;
}

// 🎯 Constantes configurables
export const LIST_CONSTANTS = {
    DEFAULT_ITEM_HEIGHT: 80,
    DEFAULT_ESTIMATE_HEIGHT: 100,
    DEFAULT_GAP: 8,
    DEFAULT_OVERSCAN: 10,
    DEFAULT_SCROLL_THRESHOLD: 200,
    LOADING_ITEM_HEIGHT: 60,
} as const;

// 🎯 Types para mejor developer experience
export type ListSize = 'sm' | 'md' | 'lg';
export type ScrollBehavior = 'auto' | 'smooth';

// 🎯 Loading state
export interface VirtualListLoadingState {
    isLoading?: boolean;
    loadingComponent?: ReactNode;
    loadingItemsCount?: number;
}

// 🎯 Extended props with loading state
export interface VirtualListPropsExtended<T = any>
    extends VirtualListProps<T>,
    VirtualListLoadingState { } 